import { isDefined, isEmpty } from "@flaminem/utils";
import { ElementDefinition } from "cytoscape";

import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.tsx";
import {
  GraphEdgeData,
  GraphNodeData,
} from "src/dynamic-view-renderer/Widgets/RelatedCases/Graph/graph.types.ts";
import { mapIcons } from "src/dynamic-view-renderer/Widgets/RelatedCases/Graph/svgs.ts";
import { RelatedCasesDisplayElementConfig } from "src/dynamic-view-renderer/Widgets/RelatedCases/relatedCases.typings.ts";
import { useT } from "src/internationalization";
import { Element } from "src/types/element";
import { PropertyValue } from "src/types/property";

import {
  createSafeNodeData,
  filterGraphElements,
  getCaseId,
  getElementId,
  getElementLabel,
  getIdentifierId,
} from "../graph.utils";

type GetRelatedCasesGraphDataInput = {
  elementsConfig: RelatedCasesDisplayElementConfig[];
  caseInfo: {
    caseId?: string;
    propertyValues: PropertyValue[];
    elements: Element[];
  };
  options?: {
    filteredIdentifiers: string[];
  };
};

export const useGetRelatedCasesGraphData = (): ((
  input: GetRelatedCasesGraphDataInput
) => ElementDefinition[]) => {
  const t = useT();
  const { graph } = useDynamicViewSchema();

  return ({
    elementsConfig,
    caseInfo,
    options,
  }: GetRelatedCasesGraphDataInput): ElementDefinition[] => {
    const currentCaseId = getCaseId({
      caseId: caseInfo.caseId,
      propertyValues: caseInfo.propertyValues,
    });

    const elementIcon = graph.getIcon("case", "element");

    const currentCaseNode = {
      data: createSafeNodeData({
        id: currentCaseId,
        label: `${t("widget.relatedCases.graph.case")}: ${currentCaseId}`,
        type: "case",
        serverId: caseInfo.caseId,
        icon: mapIcons(elementIcon),
      } as GraphNodeData),
    };

    const filteredElements = filterGraphElements(elementsConfig, caseInfo);

    const textOrEmpty = (text: string | undefined) => text?.trim() || t("general.empty");

    const elementsGraphData = elementsConfig.flatMap((elemConf) => {
      const currentCaseElement = filteredElements.find(
        (x) => x.configurableType.code === elemConf.code
      );

      if (!currentCaseElement) {
        return null;
      }

      const currentElementId = getElementId(currentCaseElement);
      const elementLabel = getElementLabel(currentCaseElement, elemConf);
      const elementIcon = graph.getIcon(currentCaseElement.schemaElement.code, "element");
      return [
        {
          data: createSafeNodeData({
            id: currentElementId,
            label: `${currentCaseElement.schemaElement.name}: ${textOrEmpty(elementLabel)}`,
            type: "element",
            code: currentCaseElement.schemaElement.code,
            serverId: caseInfo.caseId,
            icon: mapIcons(elementIcon),
          } as GraphNodeData),
        },
        {
          data: createSafeNodeData({
            id: `${currentCaseId}-${currentElementId}`,
            label: ``,
            type: "edge-case",
            source: currentCaseId,
            target: currentElementId,
          } as GraphEdgeData),
        },
      ];
    });

    const identifiersGraphData =
      filteredElements?.flatMap((element) => {
        const elementCode = element.schemaElement.code;
        return element.identifiers
          .filter(
            (identifier) =>
              isEmpty(options?.filteredIdentifiers) ||
              options?.filteredIdentifiers.includes(identifier.id)
          )
          .flatMap((identifier) => {
            const identifierId = getIdentifierId(
              identifier.value,
              identifier.schemaIdentifier.name
            );

            const identifierIcon = graph.getIcon(identifier.identifierType?.code, "identifier");

            return [
              {
                data: createSafeNodeData({
                  id: identifierId,
                  label: `${identifier.schemaIdentifier.name}: ${textOrEmpty(identifier.value)}`,
                  type: "identifier",
                  element: elementCode,
                  identifierCode: identifier.identifierType.code.toLowerCase(),
                  icon: mapIcons(identifierIcon),
                  serverId: caseInfo.caseId,
                } as GraphNodeData),
              },
              {
                data: createSafeNodeData({
                  id: `${identifierId}-${getElementId(element)}`,
                  label: ``,
                  type: "edge-elem",
                  element: elementCode,
                  target: identifierId,
                  source: getElementId(element),
                } as GraphEdgeData),
              },
            ];
          });
      }) ?? [];

    return [
      currentCaseNode,
      ...elementsGraphData.filter(isDefined),
      ...identifiersGraphData.filter(isDefined),
    ];
  };
};
